// Browser-owned preparation membership, counts, limits and table projection.
import { itemsCol, layerEntriesOf, layerHas, layerListEntryUuid, layerOf, layerValue, scalarLayerSlot } from '../../shared/array-types';
import { normalizePreparedMap, preparedIds } from './prepared-spells';
import { phpInt, spellcastingLimits, type PrepClassMeta } from './spell-prep-limits';

export const SPELL_PREP_POOL_SCHEMA = 'janus.spell_prep_pool.v3';

export interface SpellNest { items?: Record<string, unknown>; item_layer_types?: Record<string, unknown>; [key: string]: unknown }
/** One pool entry (ft_spell_prep_class_pool): PHP-read facts + the lean nest. `rcl` / `feats` are present iff the layer is. */
export interface PoolEntry {
    id: string; name: string; level: unknown; concentration: boolean; ritual: boolean;
    rcl?: unknown; feats?: string[]; nest: SpellNest;
}
export interface PoolPayload {
    schema: string; version: string;
    meta: PrepClassMeta[];
    /** Page-adopted definitions, including saved archived/deleted prepared spells. */
    definitions?: Record<string, SpellNest>;
    /** "<CLASS_ID>:<SUBCLASS_ID>" (upper-case; subclass may be empty) → entries; null = the class has no restricted spheres. */
    pools: Record<string, PoolEntry[] | null>;
    /** every spell row query_full_item_arr resolves: [level] or [level, requires_feat ids]. */
    index: Record<string, [unknown] | [unknown, string[]]>;
}

export interface PrepClass { class_number: number; class_id: string; name: string; icon_id: string }
export interface PrepSpell {
    nest: SpellNest; id: string; name: string; level: number; concentration: boolean; ritual: boolean;
    known: 'at_will' | 'spell' | null; prepared: boolean;
}
export interface PrepTable {
    classes: PrepClass[]; class_number: number; spell_list: number;
    class_id: string; spell_prepared_string: string;
    limits: { cantrips_prepared: number; max_cantrips: number; spells_prepared: number; max_spells: number };
    max_spell_level_castable: number; cantrip_button_disabled: boolean; spells_button_disabled: boolean;
    can_edit: boolean; spells: PrepSpell[]; error?: string;
}

export interface TableDeps {
    /** The full def of a spell the row's catalog holds (findFullNestedItem in the page); null when not held. */
    resolveDef(id: string): unknown;
    /** ft_name_from_blob fallback for a class id outside the PlayableClass meta. */
    nameOf(id: string): string;
}

type AnyRec = Record<string, unknown>;
const rec = (v: unknown): AnyRec | null => (v !== null && typeof v === 'object' && !Array.isArray(v) ? (v as AnyRec) : null);
const upper = (v: unknown): string => String(v ?? '').toUpperCase();
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const checkUuid = (v: unknown): v is string => typeof v === 'string' && v.trim() !== '' && UUID_RE.test(v);

/** PHP 8 numeric string (leading / trailing whitespace allowed; no hex / binary). */
const NUMERIC_RE = /^[ \t\n\r\v\f]*[+-]?(\d+(\.\d*)?|\.\d+)([eE][+-]?\d+)?[ \t\n\r\v\f]*$/;
/** PHP 8 `$a <=> $b` for a layer_value result against an int (Codex R2 review #1): numbers and numeric strings compare as
 * numbers; a non-numeric string against a number compares as strings; null / bool compare as bools. */
export function phpCmp(a: unknown, b: number): number {
    if (a === null || a === undefined || typeof a === 'boolean') { const x = !!a, y = b !== 0; return x === y ? 0 : x ? 1 : -1; }
    if (typeof a === 'number') return a === b ? 0 : a < b ? -1 : 1;
    if (typeof a === 'string') {
        if (NUMERIC_RE.test(a)) { const n = Number(a); return n === b ? 0 : n < b ? -1 : 1; }
        const bs = String(b);
        return a === bs ? 0 : a < bs ? -1 : 1;
    }
    return 1; // arrays / objects are always greater than a scalar in PHP
}
/** PHP 8 `$v == 0` (null == 0, '' != 0, '0.0' == 0). */
export const looseEqZero = (v: unknown): boolean => phpCmp(v, 0) === 0 && !(typeof v === 'string' && !NUMERIC_RE.test(v));
const looseGtZero = (v: unknown): boolean => phpCmp(v, 0) > 0;
const looseLe = (a: unknown, b: number): boolean => phpCmp(a, b) <= 0;

/** ft_prepare_class_id: a class_N slot → bare class id (nest → item_field item_id, then [0]; scalar as string). */
export function classIdOfSlot(slot: unknown): string {
    if (Array.isArray(slot)) return String(slot[0] ?? '');
    if (rec(slot)) return String(itemsCol(slot, 'item_id') ?? '');
    return slot === null || slot === undefined ? '' : String(slot);
}
function pcBucket(row: unknown): AnyRec { return rec(rec(row)?.player_characters) ?? {}; }
/** PHP empty(): null / '' / '0' / 0 / false / [] (a non-empty object is not empty). */
function phpEmpty(v: unknown): boolean {
    return v === undefined || v === null || v === '' || v === '0' || v === 0 || v === false || (Array.isArray(v) && v.length === 0);
}

/** build_caster_spell_list_arr's subclass: player_characters.subclass_N (else the legacy root subclass_N[0]) → bare UUID. */
export function subclassIdOf(row: unknown, n: number): string {
    const pc = pcBucket(row);
    const slot = pc[`subclass_${n}`] ?? (Array.isArray(rec(row)?.[`subclass_${n}`]) ? (rec(row)![`subclass_${n}`] as unknown[])[0] : undefined);
    return layerListEntryUuid(slot ?? null);
}
/** The pool-payload key of class N: "<CLASS>:<SUBCLASS>" upper-case (the endpoint's normalization). */
export function poolKeyOf(row: unknown, n: number): string {
    return `${upper(classIdOfSlot(pcBucket(row)[`class_${n}`]))}:${upper(subclassIdOf(row, n))}`;
}
/** Every pool key this row can ask for (each class_N slot) — one payload request serves the whole tab. */
export function poolKeysForRow(row: unknown): string[] {
    const pc = pcBucket(row);
    const keys = new Set<string>();
    for (let n = 1; n <= 20; n++) {
        if (phpEmpty(pc[`class_${n}`])) continue;
        const cid = classIdOfSlot(pc[`class_${n}`]);
        if (checkUuid(cid)) keys.add(poolKeyOf(row, n));
    }
    return [...keys].sort();
}

/** ft_prepare_caster_classes: class_1..20 that are bending classes, with the PlayableClass name (else the blob's). */
export function casterClasses(row: unknown, meta: readonly PrepClassMeta[], deps: Pick<TableDeps, 'nameOf'>): PrepClass[] {
    const pc = pcBucket(row);
    const out: PrepClass[] = [];
    for (let x = 1; x <= 20; x++) {
        if (phpEmpty(pc[`class_${x}`])) continue;
        const classId = classIdOfSlot(pc[`class_${x}`]);
        if (classId === '') continue;
        const info = meta.find((m) => m.item_id === classId);
        if (info && info.caster_type === 'no_bending_powers') continue;
        const name = info?.item_name || deps.nameOf(classId);
        out.push({ class_number: x, class_id: classId, name: String(name ?? ''), icon_id: classId });
    }
    return out;
}

/** layer_entries() for a PC caster's computed ability lists (computed.creature.abilities[layer], suppressed ids dropped),
 * spells_known (computed.creature.spells_known); anything else through the generic reader. */
function casterEntries(row: unknown, layer: string): unknown[] {
    const cc = rec(rec(rec(row)?.computed)?.creature);
    const abilities = rec(cc?.abilities)?.[layer];
    if (Array.isArray(abilities)) {
        const suppressed = rec(cc?.suppressed_ability_ids);
        if (!suppressed || Object.keys(suppressed).length === 0) return abilities;
        return abilities.filter((e) => { const id = abilityEntryId(e); return id === '' || !(id in suppressed); });
    }
    if ((layer === 'spells_known' || layer === 'curses_known') && Array.isArray(cc?.[layer])) return cc![layer] as unknown[];
    return layerEntriesOf(row, layer);
}
/** ability_entry_id(): string; ability_id; item_arr ref; item_id (when no item_arr key); else the entry's identity. */
function abilityEntryId(e: unknown): string {
    if (typeof e === 'string') return e;
    const r = rec(e);
    if (!r) return '';
    if (typeof r.ability_id === 'string' && r.ability_id !== '') return r.ability_id;
    if (typeof r.item_arr === 'string' && r.item_arr !== '') return r.item_arr;
    if (!('item_arr' in r) && typeof r.item_id === 'string' && r.item_id !== '') return r.item_id;
    const src = rec(r.item_arr) ?? r;
    const id = itemsCol(src, 'item_id') ?? layerListEntryUuid(src);
    return typeof id === 'string' ? id : '';
}
/** layer_value() int for a caster modifier (prepare_additional_*): item_layer_types, then computed.creature.modifiers, then
 * the generic reader — (int) of the result, 0 when absent. */
function casterModifierInt(row: unknown, name: string): number {
    const r = rec(row);
    const ilt = rec(r?.item_layer_types);
    if (ilt && name in ilt) { const v = layerValue(ilt[name] as unknown); if (v !== undefined && v !== null) return phpInt(v); }
    const m = rec(rec(rec(r?.computed)?.creature)?.modifiers)?.[name];
    if (m !== undefined && m !== null) return phpInt(layerValue(m as unknown));
    return phpInt(layerOf(row, name) ?? 0);
}

/** The pool's two caster gates (ft_class_spell_pool_arr order): requires_class_level ≤ class level, then the spell-level gate
 * (verbatim legacy: a strict-int 0 passes when cantrips are allowed, else `level !== null && level <= max`). */
function passesLevelGates(level: unknown, maxLevel: number, maxCantrips: number): boolean {
    if (level === 0 && maxCantrips > 0) return true;
    return level !== null && level !== undefined && looseLe(level, maxLevel);
}

/** Shared class-pool gates, also used by extraordinary Sanguine casts. */
export function classPoolAtLevel(pool: readonly PoolEntry[], classLevel: number, maxLevel: number, maxCantrips: number): PoolEntry[] {
    return pool.filter(e => (!('rcl' in e) || e.rcl !== null && e.rcl !== undefined && looseLe(e.rcl, classLevel))
        && passesLevelGates(e.level, maxLevel, maxCantrips));
}

/** Every required feat must be among the caster's native ability entries. */
export function casterMeetsSpellFeats(row: unknown, feats: readonly string[] = []): boolean {
    if (!feats.length) return true;
    const held = new Set(casterEntries(row, 'feat').map(abilityEntryId));
    return feats.every(f => f === '' || held.has(f));
}

export interface BuildInput {
    row: unknown;
    payload: PoolPayload;
    classNumber: number;
    spellList: number;
    /** has_player_permission(viewer, caster) — per viewer, never from the payload. */
    canEdit: boolean;
    deps: TableDeps;
}

/** ft_build_prepare_spells_data() in the browser. `error: 'pool_missing'` when the payload lacks the class's pool key. */
export function buildPrepTable(i: BuildInput): PrepTable {
    const { row, payload, deps } = i;
    const spellList = i.spellList >= 1 && i.spellList <= 3 ? Math.trunc(i.spellList) : 1;
    const classes = casterClasses(row, payload.meta, deps);
    const empty = (error: string): PrepTable => ({
        classes, class_number: 0, spell_list: spellList, class_id: '', spell_prepared_string: '',
        limits: { cantrips_prepared: 0, max_cantrips: 0, spells_prepared: 0, max_spells: 0 },
        max_spell_level_castable: 0, cantrip_button_disabled: true, spells_button_disabled: true, can_edit: i.canEdit, spells: [], error,
    });
    if (classes.length === 0) return empty('no_caster_classes');
    const classNumber = classes.some((c) => c.class_number === i.classNumber) ? i.classNumber : classes[0]!.class_number;
    const pc = pcBucket(row);
    const classId = classIdOfSlot(pc[`class_${classNumber}`]);
    if (classId === '') return empty('no_class');
    const classLevel = phpInt(pc[`class_level_${classNumber}`] ?? 0);

    // Browser-derived facts from the page-adopted catalog.
    const byId = new Map<string, PoolEntry>();
    for (const entries of Object.values(payload.pools)) for (const e of entries ?? []) if (!byId.has(upper(e.id))) byId.set(upper(e.id), e);
    const index = new Map<string, { key: string; level: unknown; feats?: string[] }>();
    for (const [id, t] of Object.entries(payload.index ?? {})) index.set(upper(id), { key: id, level: t[0], ...(t.length > 1 ? { feats: t[1] as string[] } : {}) });
    // A spell the public index does not carry (a caster's own PRIVATE spell) is read from the row's catalog def, which the viewer
    // is authorized to hold (Codex R2 review #6).
    const factOf = (id: string): { key: string; level: unknown; feats?: string[] } | null => {
        const hit = index.get(upper(id));
        if (hit) return hit;
        const def = rec(payload.definitions?.[upper(id)] ?? payload.definitions?.[id] ?? deps.resolveDef(id));
        if (!def?.item_layer_types) return null;
        const key = String(itemsCol(def, 'item_id') ?? id);
        return { key, level: layerOf(def, 'spell_level') ?? null,
            ...(layerHas(def, 'requires_feat') ? { feats: layerEntriesOf(def, 'requires_feat').map(scalarLayerSlot) } : {}) };
    };
    const levelOf = (id: string): unknown => factOf(id)?.level ?? byId.get(upper(id))?.level ?? null;

    // ── caps (build_known_spells_numbered_inc.php) ──
    const hasMap = rec(row) !== null && 'encounter_creatures_spells_prepared' in (row as AnyRec);
    const map = normalizePreparedMap(hasMap ? (row as AnyRec).encounter_creatures_spells_prepared : null);
    const viewedIds = preparedIds(map, classId, spellList);
    let cantripsPrepared = 0, spellsPrepared = 0;
    const preparedSet = new Set<string>();
    for (const id of viewedIds) {
        const lvl = levelOf(id);
        if (looseEqZero(lvl)) cantripsPrepared++;
        if (looseGtZero(lvl)) spellsPrepared++;
        if (factOf(id)) preparedSet.add(upper(id)); // resolvable ids only (PHP drops an empty query_full_item_arr)
    }
    const meta = payload.meta.find((m) => m.item_id === classId) ?? null;
    const lim = spellcastingLimits(meta, classLevel, row);
    const maxCantrips = lim.max_class_cantrips_known + casterModifierInt(row, 'prepare_additional_cantrips');
    const maxSpells = lim.max_class_spells_known + casterModifierInt(row, 'prepare_additional_spells');
    const maxLevel = lim.max_spell_level_castable;

    // ── the spell list (build_caster_spell_list_arr) ──
    const pool = payload.pools[poolKeyOf(row, classNumber)];
    if (pool === undefined) return { ...empty('pool_missing'), class_number: classNumber, class_id: classId };
    type Candidate = { id: string; level: unknown; name: string; concentration: boolean; ritual: boolean; feats?: string[]; nest: SpellNest };
    const list: Candidate[] = [];
    const poolIds = new Set<string>();
    for (const e of classPoolAtLevel(pool ?? [], classLevel, maxLevel, maxCantrips)) {
        list.push(e);
        poolIds.add(e.id);
    }
    for (const entry of casterEntries(row, 'spells_known')) {
        let knownId: string;
        if (typeof entry === 'string') knownId = entry;
        else if (rec(entry)) knownId = String(itemsCol(entry, 'item_id') ?? rec(entry)!.item_id ?? '');
        else continue;
        if (knownId === '' || poolIds.has(knownId) || !checkUuid(knownId)) continue;
        const fact = factOf(knownId);
        if (!fact || fact.level === null || fact.level === undefined) continue; // empty → skipped; no level → neither gate passes
        if (!passesLevelGates(fact.level, maxLevel, maxCantrips)) continue;
        poolIds.add(knownId);
        // Display facts: another loaded pool's entry (the server's own facts + lean nest) first, then the row's catalog def, then
        // the name alone (Codex R2 review #3). Gates always read `fact`.
        const other = byId.get(upper(fact.key));
        const def = other ? null : rec(payload.definitions?.[upper(fact.key)] ?? payload.definitions?.[fact.key] ?? deps.resolveDef(fact.key));
        const nest: SpellNest = other ? other.nest
            : def?.item_layer_types ? { items: (def.items ?? {}) as AnyRec, item_layer_types: def.item_layer_types as AnyRec }
            : { items: { item_id: fact.key } };
        list.push({
            id: fact.key, level: fact.level, nest,
            name: other ? other.name : String(itemsCol(def ?? nest, 'item_name') ?? '') || deps.nameOf(fact.key),
            concentration: other ? other.concentration : def ? layerHas(def, 'spell_concentration') : false,
            ritual: other ? other.ritual : def ? layerHas(def, 'spell_ritual') : false,
            ...(fact.feats ? { feats: fact.feats } : {}),
        });
    }
    // requires_feat gate (caster_has_spell_requires_feat): every required feat id among the caster's feats (strict).
    const gated = list.filter(s => casterMeetsSpellFeats(row, s.feats));

    // ── flags ──
    const atWill = new Set(casterEntries(row, 'at_will_spell').map((aw) => (rec(aw) ? String(itemsCol(aw, 'item_id') ?? '') : String(aw ?? ''))).filter(Boolean));
    const knownIds = new Set(casterEntries(row, 'spells_known').map((sk) => (rec(sk) ? String(itemsCol(sk, 'item_id') ?? rec(sk)!.item_id ?? '') : String(sk ?? ''))).filter(Boolean));
    const activeList = phpInt(rec(rec(row)?.encounter_creatures)?.spell_list_prepared ?? 1); // PHP (int)(… ?? 1): 0 stays 0
    for (const id of preparedIds(map, classId, activeList)) knownIds.delete(id);

    const spells: PrepSpell[] = gated.map((s) => {
        const known: PrepSpell['known'] = atWill.has(s.id) ? 'at_will' : knownIds.has(s.id) ? 'spell' : null;
        return {
            nest: s.nest, id: s.id, name: s.name, level: phpInt(s.level ?? 0),
            concentration: s.concentration, ritual: s.ritual,
            known, prepared: known === null && preparedSet.has(upper(s.id)),
        };
    });
    spells.sort((a, b) => a.level - b.level); // stable, like PHP 8 usort

    return {
        classes, class_number: classNumber, spell_list: spellList, class_id: classId,
        spell_prepared_string: `spell_prepared_${spellList}_class_${classNumber}`,
        limits: { cantrips_prepared: cantripsPrepared, max_cantrips: maxCantrips, spells_prepared: spellsPrepared, max_spells: maxSpells },
        max_spell_level_castable: maxLevel,
        cantrip_button_disabled: cantripsPrepared >= maxCantrips,
        spells_button_disabled: spellsPrepared >= maxSpells,
        can_edit: i.canEdit, spells,
    };
}
